import React, { useState, useEffect } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  addDoc,
  query,
  where,
} from "firebase/firestore";

const AdminClassroom = () => {
  const db = getFirestore();
  const [educatorCode, setEducatorCode] = useState("All");
  const [educatorList, setEducatorList] = useState([]);
  const [grade, setGrade] = useState("");
  const [state, setState] = useState("");
  const [subject, setSubject] = useState("Science");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [musicUrl, setMusicUrl] = useState("");
  const [gameUrl, setGameUrl] = useState("");
  const [customMaterials, setCustomMaterials] = useState([]);

  useEffect(() => {
    const fetchEducators = async () => {
      try {
        // This used to be:
        //   where("role", "array-contains", "educator")
        // but `role` is a string ("va-3rd-educator"), not an array, so
        // array-contains matched zero documents and this dropdown was always
        // empty. Firestore has no "string contains" operator, so the filter
        // has to happen client-side after fetching.
        const querySnapshot = await getDocs(collection(db, "users"));
        const educators = querySnapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => String(u.role || "").toLowerCase().includes("educator"))
          .filter((u) => u.educatorCode)
          .map((u) => ({
            educatorCode: u.educatorCode,
            grade: u.grade,
            state: u.state,
          }));
        setEducatorList(educators);
      } catch (error) {
        console.error("Error fetching educators:", error);
      }
    };

    const fetchMaterials = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "customMaterials"));
        const materials = querySnapshot.docs.map(doc => doc.data());
        setCustomMaterials(materials);
      } catch (error) {
        console.error("Error fetching materials:", error);
      }
    };

    fetchEducators();
    fetchMaterials();
  }, [db]);

  const handleEducatorChange = (code) => {
    setEducatorCode(code);
    if (code === "All") {
      setGrade("");
      setState("");
    } else {
      const educator = educatorList.find((e) => e.educatorCode === code);
      if (educator) {
        setGrade(educator.grade);
        setState(educator.state);
      }
    }
  };

  const handleAddMaterial = async () => {
    if (!title || !description) {
      alert("Title and Description are required.");
      return;
    }

    const materialData = {
      educatorCode,
      grade: educatorCode === "All" ? grade : grade,
      state: educatorCode === "All" ? state : state,
      subject,
      title,
      description,
      videoUrl,
      musicUrl,
      gameUrl,
      timestamp: new Date(),
    };

    try {
      await addDoc(collection(db, "customMaterials"), materialData);
      setCustomMaterials((prev) => [...prev, materialData]);
      alert("Material added successfully!");
      setTitle("");
      setDescription("");
      setVideoUrl("");
      setMusicUrl("");
      setGameUrl("");
    } catch (error) {
      console.error("Error adding material:", error);
    }
  };

  return (
    <div style={styles.container}>
      <h1>Admin Classroom - Add Custom Material</h1>

      {/* Educator Code Dropdown */}
      <label>Educator Code:</label>
      <select value={educatorCode} onChange={(e) => handleEducatorChange(e.target.value)}>
        <option value="All">All</option>
        {educatorList.map((educator, index) => (
          <option key={index} value={educator.educatorCode}>
            {educator.educatorCode}
          </option>
        ))}
      </select>

      {/* Auto-filled Grade & State (Only if specific educator is selected) */}
      {educatorCode !== "All" && (
        <>
          <p>Grade: {grade || "N/A"}</p>
          <p>State: {state || "N/A"}</p>
        </>
      )}

      {/* If "All" is selected, allow manual selection */}
      {educatorCode === "All" && (
        <>
          <label>Grade:</label>
          <select value={grade} onChange={(e) => setGrade(e.target.value)}>
            <option value="">Select Grade</option>
            <option value="1st">1st</option>
            <option value="2nd">2nd</option>
            <option value="3rd">3rd</option>
            <option value="4th">4th</option>
            <option value="5th">5th</option>
          </select>
          <label>State:</label>
          <input type="text" value={state} onChange={(e) => setState(e.target.value)} />
        </>
      )}

      {/* Subject Dropdown */}
      <label>Subject:</label>
      <select value={subject} onChange={(e) => setSubject(e.target.value)}>
        <option value="Science">Science</option>
        <option value="Social Studies">Social Studies</option>
        <option value="Math">Math</option>
        <option value="Other">Other</option>
      </select>

      {/* Custom Material Fields */}
      <label>Title:</label>
      <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />

      <label>Description:</label>
      <textarea value={description} onChange={(e) => setDescription(e.target.value)} />

      <label>Video URL:</label>
      <input type="text" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} />

      <label>Music URL:</label>
      <input type="text" value={musicUrl} onChange={(e) => setMusicUrl(e.target.value)} />

      <label>Game URL:</label>
      <input type="text" value={gameUrl} onChange={(e) => setGameUrl(e.target.value)} />

      <button onClick={handleAddMaterial}>Add Material</button>

      {/* Display Custom Materials */}
      <h2>Custom Materials</h2>
      <div style={styles.materialList}>
        {customMaterials.map((material, index) => (
          <div key={index} style={styles.materialItem}>
            <h3>{material.title}</h3>
            <p><strong>Subject:</strong> {material.subject}</p>
            <p><strong>Description:</strong> {material.description}</p>
            <p><strong>Grade:</strong> {material.grade}</p>
            <p><strong>State:</strong> {material.state}</p>
            <p><strong>Educator Code:</strong> {material.educatorCode}</p>
            <p>
              <a href={material.videoUrl} target="_blank" rel="noopener noreferrer">Video</a> | 
              <a href={material.musicUrl} target="_blank" rel="noopener noreferrer">Music</a> | 
              <a href={material.gameUrl} target="_blank" rel="noopener noreferrer">Game</a>
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  container: { padding: "20px", fontFamily: "Arial" },
  materialList: { marginTop: "20px" },
  materialItem: {
    border: "1px solid #ddd",
    padding: "10px",
    marginBottom: "10px",
    borderRadius: "5px",
  },
};

export default AdminClassroom;
